/* @path: /src/pages/shelbula/workspaces/WorkspaceSidebar.jsx */
// @version: 3.4 (Force Pull Button)
// @description: Changes the "Fetch" button to a destructive "Force Pull" with warning styles.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Building2, Settings, Github, Copy, Check, Download, GitBranchPlus, ExternalLink, Loader, AlertTriangle, CheckCircle, AlertCircle, HardDrive, Wrench, ChevronDown, Search, Globe, Image, Database, RefreshCw, ArrowLeft, EthernetPort, Proportions, FolderOpen, Trash2, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { keyStateManager } from '@/managers/zKeysManager';
import { authManager, TIERS } from '@/managers/AuthManager';
import { botManager } from '@/managers/BotManager';
import { useConversation } from '@/managers/ConvoManager';
import { workspaceManager } from '@/managers/workspaceManager';
import { areWorkspaceContextIdsEqual, normalizeWorkspaceContextId } from '@/utils/workspaceContext';
import NextMessageOptionsModal from '@/components/tools/NextMessageOptionsModal';
import { applyToolPresentationOverrides, isMaintenanceTool, isWorkspaceMemoryTool, shouldHideToolFromOptions } from '@/utils/toolPresentation';
import { getEffectiveActiveServerIds, getEffectiveActiveToolIds, getWorkspaceForConversation, resolveWorkspaceAccessScopeId } from '@/utils/workspaceAccessSettings';
import { updateUserKeys, deleteUserApiKey } from './workspaceAccountManagement';
import ContextMenu from './ContextMenu';
import StatusDisplay from './StatusDisplay';

const ShimmeringPlaceholder = ({ className }) => (
    <div className={`relative overflow-hidden bg-surface-dark ${className}`}>
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
    </div>
);

const SidebarSkeleton = () => (
    <div>
        {[...Array(8)].map((_, i) => (
            <div key={i} className="w-full text-left px-3 py-2.5 flex items-center gap-2.5 border-b border-border-color/50">
                <ShimmeringPlaceholder className="w-2 h-2 rounded-full" />
                <div className="flex-1 overflow-hidden space-y-1.5">
                    <ShimmeringPlaceholder className="h-2.5 rounded w-4/5" />
                    <ShimmeringPlaceholder className="h-2 rounded w-3/5" />
                </div>
            </div>
        ))}
    </div>
);

const formatRelativeDate = (timestamp) => {
    let date;
    if (timestamp?.toDate) {
        date = timestamp.toDate();
    } else if (timestamp && typeof timestamp._seconds === 'number') {
        date = new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
    } else if (timestamp instanceof Date) {
        date = timestamp;
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        date = new Date(timestamp);
    } else {
        return 'No Activity Yet';
    }

    if (Number.isNaN(date.getTime())) {
        return 'No Activity Yet';
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays <= 30) return `${diffInDays} days ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getConversationTimestamp = (conversation) => {
    return conversation?.lastUpdated || conversation?.lastMessagePreview?.timestamp || conversation?.created || null;
};

const getWorkspaceEffectiveLastUsed = (workspace, conversations = {}) => {
    if (!workspace) {
        return null;
    }

    if (!workspace.isDefault) {
        return workspace.lastModified || null;
    }

    const matchingConversations = Object.values(conversations || {}).filter(
        (conversation) => areWorkspaceContextIdsEqual(conversation?.workspaceContextId, workspace.id)
    );

    if (matchingConversations.length === 0) {
        return null;
    }

    return matchingConversations
        .map(getConversationTimestamp)
        .filter(Boolean)
        .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] || null;
};


const GithubPatManager = ({ isGithubPatReady }) => {
    const [patInput, setPatInput] = useState('');
    const [isKeySet, setIsKeySet] = useState(false);
    const [saveStatus, setSaveStatus] = useState('idle');
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setIsKeySet(isGithubPatReady);
        setPatInput('');
        setIsDirty(false);
    }, [isGithubPatReady]);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setPatInput(newValue);
        setIsDirty(true);
        setSaveStatus('idle');
    };

    const handleSave = async () => {
        if (saveStatus === 'loading') return;
    
        setSaveStatus('loading');
        try {
            if (!patInput.trim()) {
                await deleteUserApiKey('GITHUB');
                setIsKeySet(false);
                setPatInput('');
                setSaveStatus('idle');
            } else {
                await updateUserKeys({ GITHUB: patInput });
                await keyStateManager.refreshFromDB();
                setIsKeySet(true);
                setPatInput('');
                setSaveStatus('success');
                setTimeout(() => setSaveStatus('idle'), 2000);
            }
            setIsDirty(false);
        } catch (error) {
            console.error("Failed to update GitHub PAT:", error);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
        }
    };
    
    const handleClear = async () => {
        if (saveStatus === 'loading') return;
        
        setSaveStatus('loading');
        try {
            await deleteUserApiKey('GITHUB');
            await keyStateManager.refreshFromDB();
            setIsKeySet(false);
            setPatInput('');
            setSaveStatus('idle');
            setIsDirty(false);
        } catch (error) {
            console.error("Failed to delete GitHub PAT:", error);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
        }
    };

    return (
        <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
        >
            <div className="p-3 bg-surface-dark/30 border-y border-border-color space-y-3">
                <p className="text-xs text-on-surface-dim">
                    Connect your GitHub account with a Personal Access Token (PAT) to enable repository integrations.
                </p>
                
                {isKeySet && !isDirty ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs" style={{ borderColor: 'rgba(var(--success-rgb), 0.3)', backgroundColor: 'rgba(var(--success-rgb), 0.1)' }}>
                            <CheckCircle className="w-3.5 h-3.5 text-success flex-shrink-0" />
                            <span className="text-on-surface font-medium">GitHub PAT is configured</span>
                        </div>
                        <button 
                            onClick={handleClear}
                            disabled={saveStatus === 'loading'}
                            className="w-full flex items-center gap-2.5 px-1 py-1.5 text-xs font-medium rounded-md hover:bg-surface-dark text-error transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                            <div className="w-4 h-4 flex items-center justify-center ml-0.5">
                                {saveStatus === 'loading' ? <Loader size={14} className="animate-spin"/> : <AlertTriangle size={14}/>}
                            </div>
                            <span className="truncate">{saveStatus === 'loading' ? 'Clearing...' : 'Clear & Reset Token'}</span>
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="relative">
                            <input
                                id="githubPatSidebar"
                                type="password"
                                value={patInput}
                                onChange={handleInputChange}
                                placeholder="ghp_..."
                                className="w-full px-3 pr-10 py-2 bg-background-darker border border-border-color rounded-md text-on-surface placeholder:text-on-surface-dim/50 focus:outline-none focus:ring-1 focus:ring-primary text-sm"
                                autoComplete="off"
                                spellCheck="false"
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                {saveStatus === 'loading' && <Loader className="animate-spin w-4 h-4 text-primary" />}
                                {saveStatus === 'success' && <Check className="w-4 h-4 text-success" />}
                                {saveStatus === 'error' && <AlertTriangle className="w-4 h-4 text-error" />}
                            </div>
                        </div>
                        
                        <button
                            onClick={handleSave}
                            disabled={!isDirty || saveStatus === 'loading'}
                            className="w-full flex items-center gap-2.5 px-1 py-1.5 text-xs font-medium rounded-md hover:bg-surface-dark text-on-surface transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                            <div className="w-4 h-4 flex items-center justify-center ml-0.5">
                                {saveStatus === 'loading' ? <Loader size={14} className="animate-spin"/> : <Check size={14}/>}
                            </div>
                            <span className="truncate">{saveStatus === 'loading' ? 'Saving...' : 'Save Token'}</span>
                        </button>
                    </>
                )}
                
                <a 
                    href="https://github.com/settings/personal-access-tokens/new?scopes=repo&description=Shelbula%20Workspaces" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-xs text-primary hover:text-primary-light hover:underline whitespace-nowrap flex items-center gap-1"
                >
                    Get a new Token <ExternalLink className="w-3 h-3" />
                </a>
            </div>
        </motion.div>
    );
};

const NotificationArea = ({ notification }) => {
    return (
        <AnimatePresence>
            {notification.show && notification.type !== 'error' && (
                <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: 10, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                >
                    <div className="mx-2.5 my-2 p-2.5 rounded-md border flex items-start gap-2.5 text-xs" style={{ borderColor: 'rgba(var(--success-rgb), 0.4)', backgroundColor: 'rgba(var(--success-rgb), 0.1)' }}>
                        <div className="flex-shrink-0 mt-0.5">
                            <CheckCircle size={14} className="text-success" />
                        </div>
                        <div className="flex-1 text-on-surface-dim font-medium">{notification.message}</div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};


const WorkspaceListItem = React.memo(({ workspace, onSelect, onContextMenu, isActive = false }) => {
    const { id, workspaceName, lastModified, repoUrl, isDefault } = workspace;
    const { conversations } = useConversation();
    const displayName = isDefault ? 'Main Workspace' : (workspaceName || id);
    const effectiveLastUsed = useMemo(() => {
        return getWorkspaceEffectiveLastUsed(workspace, conversations);
    }, [conversations, workspace]);

    const handleContextMenu = useCallback((event) => {
        if (!onContextMenu) {
            return;
        }

        event.preventDefault();
        onContextMenu(event.clientX, event.clientY, workspace);
    }, [onContextMenu, workspace]);
    
    return (
        <button onClick={() => onSelect(id)} onContextMenu={handleContextMenu} className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 transition-colors border-b border-border-color ${isActive ? 'bg-surface-dark' : 'hover:bg-surface-dark'}`}>
            <div className="flex-1 overflow-hidden">
                <p className="font-medium text-[12px] leading-[1.2] text-on-surface truncate">{displayName}</p>
                <div className="flex items-center gap-1.5 text-on-surface-dim">
                    <span className="text-[10px] leading-none">{formatRelativeDate(effectiveLastUsed)}</span>
                </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-on-surface-dim">
                {isActive ? <CheckCircle size={13} className="text-primary" title="Selected" /> : repoUrl ? <Github size={13} title="GitHub Connected" /> : <HardDrive size={13} title={isDefault ? 'Main Workspace' : 'Not GitHub Connected'} />}
            </div>
        </button>
    );
});
WorkspaceListItem.displayName = 'WorkspaceListItem';

const WorkspaceListHeader = ({ onInitiateCreate, createdCount, workspaceLimit }) => (
    <div className="h-[50px] px-3 flex-shrink-0 flex items-center justify-between border-b border-border-color">
        <div className="flex items-center gap-2 p-0">
            <Building2 size={14} className="text-primary" />
            <h2 className="text-sm font-semibold text-on-surface">Workspaces</h2>
        </div>
        <div className="flex items-center gap-1.5">
            {workspaceLimit !== undefined && workspaceLimit !== Infinity && (
                <span className="text-[10px] text-on-surface-dim">{createdCount + 1}/{workspaceLimit}</span>
            )}
            <button onClick={onInitiateCreate} className="p-1 rounded-full text-on-surface-dim hover:text-primary transition-colors" title="Create a new workspace">
                <Plus size={16} />
            </button>
        </div>
    </div>
);

const WorkspaceDetailHeader = ({ workspaceName, onBack, isDefault = false }) => (
    <div className="h-[50px] px-3 flex-shrink-0 flex items-center gap-2 border-b border-border-color">
        <button
            type="button"
            onClick={onBack}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-on-surface-dim transition-colors hover:bg-surface-dark hover:text-on-surface"
            aria-label="Back to workspaces"
            title="Back to workspaces"
        >
            <ArrowLeft size={14} />
        </button>
        <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-on-surface-dim">Workspace</div>
        </div>
    </div>
);

const WorkspaceListView = ({ workspaces, activeWorkspaceId, onSelect, onContextMenu, isLoading, error, onInitiateCreate, className = '' }) => {
    return (
    <>
        <div className={`overflow-y-auto custom-scrollbar ${className}`} onClick={() => onContextMenu?.(0, 0, null)}>
            {isLoading ? <SidebarSkeleton /> : error ? (
                <div className="p-4 text-center text-sm text-red-400"><p>Failed to load:</p><p className="mt-1 font-mono text-xs">{error}</p></div>
            ) : workspaces?.length > 0 ? (
                workspaces.map(ws => <WorkspaceListItem key={ws.id} workspace={ws} onSelect={onSelect} onContextMenu={onContextMenu} isActive={activeWorkspaceId === ws.id} />)
            ) : (
                <div className="h-full flex flex-col items-center justify-center">
                    <div className="p-4 text-center">
                        <h3 className="font-semibold text-on-surface">Get Started</h3>
                        <p className="text-xs text-on-surface-dim mt-2 mb-4">
                            The Main Workspace stays shared. Create another workspace when you want that work isolated.
                        </p>
                        <button 
                            onClick={onInitiateCreate} 
                            className="mt-2 px-4 py-2 text-sm font-semibold rounded-md flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-dark transition-colors mx-auto"
                        >
                            <Plus size={16} />
                                Create Workspace
                        </button>
                    </div>
                </div>
            )}
        </div>
    </>
    );
};

const CompactAccessSwitch = ({ label, enabled, disabled, onChange, note }) => (
    <div className="flex items-center gap-2 px-1 py-1.5">
        <div className="min-w-0 flex-1">
            <div className={`text-[11px] font-medium truncate ${disabled ? 'text-on-surface-dim/60' : 'text-on-surface'}`}>{label}</div>
            {note ? <div className="text-[10px] text-on-surface-dim truncate">{note}</div> : null}
        </div>
        <button
            type="button"
            onClick={onChange}
            disabled={disabled}
            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            aria-checked={enabled}
            role="switch"
            style={{
                backgroundColor: '#6b7280',
                border: '1px solid #4b5563',
                boxShadow: 'inset 0 0 0 1px #374151'
            }}
        >
            <span
                className={`inline-block h-3 w-3 transform rounded-full shadow transition-transform ${enabled ? 'translate-x-3.5' : 'translate-x-0.5'}`}
                style={{
                    backgroundColor: enabled ? 'rgb(var(--primary-rgb))' : '#d1d5db'
                }}
            />
        </button>
    </div>
);

const WorkspaceAccessControls = () => {
    const { currentConversation, updateConversation, createConversation } = useConversation();
    const isIncognitoActive = currentConversation?.privacyMode === 'incognito';
    const handleIncognitoToggle = useCallback(async () => {
        if (isIncognitoActive) {
            // Toggle OFF — return to a normal chat scoped to the current sidebar workspace
            const workspaceId = workspaceManager.getState().activeWorkspaceId || null;
            await createConversation(workspaceId ? { workspaceContextId: workspaceId } : {});
        } else {
            // Toggle ON — start a new incognito chat
            await createConversation({ privacyMode: 'incognito', incognitoMode: true });
        }
    }, [createConversation, isIncognitoActive]);
    const [authState, setAuthState] = useState(() => authManager.getState());
    const [botManagerState, setBotManagerState] = useState(() => botManager.getState());
    const [workspaceState, setWorkspaceState] = useState(() => workspaceManager.getState());
    const [optimisticToolStates, setOptimisticToolStates] = useState({});
    const [optimisticMcpStates, setOptimisticMcpStates] = useState({});
    const [isMcpManagerModalOpen, setIsMcpManagerModalOpen] = useState(false);

    useEffect(() => {
        const unsubAuth = authManager.subscribe(setAuthState);
        const unsubBot = botManager.subscribe(setBotManagerState);
        const unsubWorkspace = workspaceManager.subscribe(setWorkspaceState);
        return () => {
            unsubAuth();
            unsubBot();
            unsubWorkspace();
        };
    }, []);

    const currentConversationId = currentConversation?.id;
    const platformModelsWithKeys = botManagerState.platformModelsWithKeys || [];
    const hiddenToolIds = useMemo(() => new Set(['Detect_And_Label']), []);
    const presentTool = useCallback((tool) => {
        return applyToolPresentationOverrides(tool);
    }, []);

    const availableTools = useMemo(() => {
        const platformsWithKeys = new Set(platformModelsWithKeys.map((model) => model.platform?.toUpperCase()).filter(Boolean));
        const hasAnyKey = platformsWithKeys.size > 0;
        const userIsBetaMember = authState.settings?.beta === true;

        return [...(authState.globalTools || [])]
            .map((rawTool) => {
                const tool = presentTool(rawTool);
                const tiers = tool.tiers || [];
                const hasAccess = tiers.length === 0 || tiers.some((tier) => authManager.hasTierAccess(tier));
                let keyDisabledReason = null;

                if (tool.platform) {
                    const requiredPlatform = tool.platform.toUpperCase();
                    if (!platformsWithKeys.has(requiredPlatform)) {
                        keyDisabledReason = `${tool.platform} key required`;
                    }
                } else if (!hasAnyKey) {
                    keyDisabledReason = 'API key required';
                }

                const isBetaRestricted = tool.beta && !userIsBetaMember;

                return {
                    id: tool.nameToPass,
                    name: tool.name,
                    description: tool.description,
                    activeByDefault: tool.activeByDefault === undefined ? true : Boolean(tool.activeByDefault),
                    isMaintenance: Boolean(tool.maintenanceMode) || isMaintenanceTool(tool),
                    maintenanceLabel: tool.maintenanceLabel || 'MAINT.',
                    isDisabled: Boolean(tool.maintenanceMode) || isMaintenanceTool(tool) || !hasAccess || Boolean(keyDisabledReason) || isBetaRestricted,
                    disabledReason: Boolean(tool.maintenanceMode) || isMaintenanceTool(tool)
                        ? 'Temporarily unavailable'
                        : isBetaRestricted
                            ? 'Closed beta'
                            : keyDisabledReason,
                    order: tool.order,
                };
            })
            .filter((tool) => !hiddenToolIds.has(tool.id) && !shouldHideToolFromOptions(tool))
            .sort((a, b) => {
                if (a.isDisabled !== b.isDisabled) return a.isDisabled - b.isDisabled;
                const orderA = a.order !== undefined ? a.order : Infinity;
                const orderB = b.order !== undefined ? b.order : Infinity;
                if (orderA !== orderB) return orderA - orderB;
                return a.name.localeCompare(b.name);
            });
    }, [authState.globalTools, authState.settings?.beta, hiddenToolIds, platformModelsWithKeys, presentTool]);

    const allUserMcpServers = useMemo(() => {
        return (authState.mcpServers || []).map((server) => ({
            ...server,
            enabled: server.enabled === undefined ? true : Boolean(server.enabled),
        }));
    }, [authState.mcpServers]);

    const areAccessSettingsLoading = !authState.isInitialized || !authState.settingsLoaded || !authState.mcpServersLoaded || !authState.globalToolsLoaded;
    const scopedWorkspaceId = useMemo(() => {
        return resolveWorkspaceAccessScopeId(currentConversation)
            || normalizeWorkspaceContextId(workspaceState.activeWorkspaceId);
    }, [currentConversation, workspaceState.activeWorkspaceId]);
    const scopedWorkspace = useMemo(() => {
        const workspaceFromConversation = getWorkspaceForConversation(workspaceState, currentConversation);
        if (workspaceFromConversation) {
            return workspaceFromConversation;
        }

        if (workspaceState.activeWorkspaceDetail?.id === scopedWorkspaceId) {
            return workspaceState.activeWorkspaceDetail;
        }

        return workspaceState.workspaces.find((workspace) => workspace.id === scopedWorkspaceId) || null;
    }, [workspaceState, currentConversation, scopedWorkspaceId]);
    const defaultActiveToolIds = useMemo(() => availableTools.filter((entry) => entry.activeByDefault && !entry.isDisabled).map((entry) => entry.id), [availableTools]);
    const defaultActiveServerIds = useMemo(() => allUserMcpServers.filter((entry) => entry.enabled).map((entry) => entry.id), [allUserMcpServers]);
    const effectiveActiveToolIds = useMemo(() => getEffectiveActiveToolIds({
        conversation: currentConversation,
        workspace: scopedWorkspace,
        fallbackActiveToolIds: defaultActiveToolIds,
    }), [currentConversation, scopedWorkspace, defaultActiveToolIds]);
    const effectiveActiveServerIds = useMemo(() => getEffectiveActiveServerIds({
        conversation: currentConversation,
        workspace: scopedWorkspace,
        fallbackActiveServerIds: defaultActiveServerIds,
    }), [currentConversation, scopedWorkspace, defaultActiveServerIds]);

    const persistAccessSettings = useCallback(async (partialSettings) => {
        if (scopedWorkspaceId) {
            await workspaceManager.updateWorkspaceAccessSettings(scopedWorkspaceId, partialSettings);
            return;
        }

        if (!currentConversationId || !currentConversation) {
            return;
        }

        const conversationPayload = {};
        if (Object.prototype.hasOwnProperty.call(partialSettings, 'activeToolIds')) {
            conversationPayload.toolSettings = {
                ...(currentConversation.toolSettings || {}),
                activeToolIds: partialSettings.activeToolIds,
            };
        }
        if (Object.prototype.hasOwnProperty.call(partialSettings, 'activeServerIds')) {
            conversationPayload.mcpSettings = {
                ...(currentConversation.mcpSettings || {}),
                activeServerIds: partialSettings.activeServerIds,
            };
        }

        if (Object.keys(conversationPayload).length > 0) {
            await updateConversation(currentConversationId, conversationPayload);
        }
    }, [currentConversation, currentConversationId, scopedWorkspaceId, updateConversation]);

    useEffect(() => {
        if (areAccessSettingsLoading || effectiveActiveToolIds.length === 0) {
            return;
        }

        const disabledToolIds = new Set(availableTools.filter((tool) => tool.isDisabled).map((tool) => tool.id));
        const nextActiveToolIds = effectiveActiveToolIds.filter((toolId) => !disabledToolIds.has(toolId));

        if (nextActiveToolIds.length !== effectiveActiveToolIds.length) {
            persistAccessSettings({ activeToolIds: nextActiveToolIds }).catch((error) => {
                console.error('Failed to remove disabled workspace tools from access settings:', error);
            });
        }
    }, [areAccessSettingsLoading, availableTools, effectiveActiveToolIds, persistAccessSettings]);

    const handleToolToggle = useCallback(async (toolIdToToggle) => {
        if (!scopedWorkspaceId && (!currentConversationId || !currentConversation)) return;
        const tool = availableTools.find((entry) => entry.id === toolIdToToggle);
        if (!tool || tool.isDisabled) return;
        if (isIncognitoActive && isWorkspaceMemoryTool(tool)) return;

        const effectiveCurrentIds = new Set(effectiveActiveToolIds);

        const targetState = !effectiveCurrentIds.has(toolIdToToggle);
        setOptimisticToolStates((prev) => ({ ...prev, [toolIdToToggle]: targetState }));

        const newActiveToolIds = new Set(effectiveCurrentIds);
        if (targetState) newActiveToolIds.add(toolIdToToggle);
        else newActiveToolIds.delete(toolIdToToggle);

        const newActiveToolIdsArray = Array.from(newActiveToolIds);

        try {
            await persistAccessSettings({ activeToolIds: newActiveToolIdsArray });
        } catch (error) {
            console.error('Failed to update conversation tool settings:', error);
        } finally {
            setOptimisticToolStates((prev) => {
                const next = { ...prev };
                delete next[toolIdToToggle];
                return next;
            });
        }
    }, [availableTools, currentConversation, currentConversationId, effectiveActiveToolIds, isIncognitoActive, persistAccessSettings, scopedWorkspaceId]);

    const handleMcpServerToggle = useCallback(async (serverIdToToggle) => {
        if (!scopedWorkspaceId && (!currentConversationId || !currentConversation)) return;
        const server = allUserMcpServers.find((entry) => entry.id === serverIdToToggle);
        if (!server) return;

        const effectiveCurrentIds = new Set(effectiveActiveServerIds);

        const targetState = !effectiveCurrentIds.has(serverIdToToggle);
        setOptimisticMcpStates((prev) => ({ ...prev, [serverIdToToggle]: targetState }));

        const newActiveIds = new Set(effectiveCurrentIds);
        if (targetState) newActiveIds.add(serverIdToToggle);
        else newActiveIds.delete(serverIdToToggle);

        const newActiveServerIdsArray = Array.from(newActiveIds);

        try {
            await persistAccessSettings({ activeServerIds: newActiveServerIdsArray });
        } catch (error) {
            console.error('Failed to update conversation MCP settings:', error);
        } finally {
            setOptimisticMcpStates((prev) => {
                const next = { ...prev };
                delete next[serverIdToToggle];
                return next;
            });
        }
    }, [allUserMcpServers, currentConversation, currentConversationId, effectiveActiveServerIds, persistAccessSettings, scopedWorkspaceId]);

    const toolRows = availableTools.map((tool) => {
        const isWorkspaceMemoryToggle = isWorkspaceMemoryTool(tool);
        const isForcedOffForIncognito = isIncognitoActive && isWorkspaceMemoryToggle;
        let enabled = false;
        if (!tool.isDisabled && !isForcedOffForIncognito) {
            if (optimisticToolStates.hasOwnProperty(tool.id)) {
                enabled = optimisticToolStates[tool.id];
            } else if (effectiveActiveToolIds.includes(tool.id)) {
                enabled = true;
            } else {
                enabled = false;
            }
        }

        return (
            <CompactAccessSwitch
                key={tool.id}
                label={(
                    <span className="flex items-center gap-1.5">
                        <span>{tool.name}</span>
                        {tool.isMaintenance ? (
                            <span className="text-[8px] px-1 py-[1px] rounded-sm uppercase tracking-wide" style={{ background: 'rgba(var(--primary-rgb), 0.15)', color: 'rgba(var(--primary-rgb), 0.7)' }}>
                                {tool.maintenanceLabel}
                            </span>
                        ) : null}
                    </span>
                )}
                note={isForcedOffForIncognito ? 'Incognito: all workspace access is disabled' : (tool.isDisabled ? tool.disabledReason : tool.description)}
                enabled={enabled}
                disabled={isForcedOffForIncognito || tool.isDisabled || (!scopedWorkspaceId && !currentConversationId)}
                onChange={() => handleToolToggle(tool.id)}
            />
        );
    });

    const mcpRows = allUserMcpServers.map((server) => {
        let enabled;
        if (optimisticMcpStates.hasOwnProperty(server.id)) {
            enabled = optimisticMcpStates[server.id];
        } else if (effectiveActiveServerIds.includes(server.id)) {
            enabled = true;
        } else {
            enabled = false;
        }

        return (
            <CompactAccessSwitch
                key={server.id}
                label={server.name}
                enabled={enabled}
                disabled={!scopedWorkspaceId && !currentConversationId}
                onChange={() => handleMcpServerToggle(server.id)}
            />
        );
    });

    return (
        <div className="p-2.5 border-b border-border-color space-y-2">
            {areAccessSettingsLoading ? (
                <div className="px-1.5 py-1 text-[11px] text-on-surface-dim">Loading...</div>
            ) : (
                <>
                    <div className="space-y-0.5">
                        <div className="flex items-center justify-between px-1.5 pb-1">
                            <h4 className="flex items-center gap-2 text-[11px] font-semibold text-on-surface-dim uppercase tracking-wider"><Wrench size={11}/><span>Built-in Tools</span></h4>
                            <div className="flex items-center gap-0.5">
                                <button
                                    type="button"
                                    onClick={handleIncognitoToggle}
                                    className={`p-1 rounded-full transition-colors ${
                                        isIncognitoActive
                                            ? 'text-violet-400 bg-violet-500/15'
                                            : 'text-on-surface-dim hover:text-violet-400 hover:bg-white/5'
                                    }`}
                                    title={isIncognitoActive ? 'Exit incognito — resume workspace chat' : 'New incognito chat (workspace context off, tools active)'}
                                >
                                    <EyeOff size={11} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsMcpManagerModalOpen(true)}
                                    className="p-1 rounded-full text-on-surface-dim hover:text-primary hover:bg-white/5 transition-colors"
                                    title="Manage tools"
                                >
                                    <Settings size={12} />
                                </button>
                            </div>
                        </div>
                        {toolRows}
                    </div>
                    <div className={`border-t border-border-color space-y-0.5 ${!authManager.hasTierAccess(TIERS.PRO) ? 'opacity-40' : ''}`}>
                        <div className="flex items-center justify-between px-1.5 pt-2 pb-1">
                            <h4 className="flex items-center gap-2 text-xs font-semibold text-on-surface-dim uppercase tracking-wider"><EthernetPort size={11}/><span>MCP</span></h4>
                            <button
                                type="button"
                                onClick={() => setIsMcpManagerModalOpen(true)}
                                className="p-1 rounded-full text-on-surface-dim hover:text-primary hover:bg-white/5 transition-colors"
                                title="Manage MCP connections"
                            >
                                <Settings size={12} />
                            </button>
                        </div>
                        {mcpRows.length > 0 ? mcpRows : <div className="px-1.5 py-1 text-[11px] text-on-surface-dim">No MCP connections configured.</div>}
                    </div>
                </>
            )}
            <NextMessageOptionsModal
                isOpen={isMcpManagerModalOpen}
                onClose={() => setIsMcpManagerModalOpen(false)}
            />
        </div>
    );
};

const CommandBarView = ({ activeWorkspace, onOpenSettings, onDownloadZip, isGithubPatReady, onInitiateClone, onInitiateCreateRepo, isCloning, isCreatingRepo, notification, onInitiateForcePull, isFetching }) => {
    const { workspaceName, id, repoUrl, fileCount, lastModified } = activeWorkspace;
    const { currentConversation } = useConversation();
    const isIncognitoActive = currentConversation?.privacyMode === 'incognito';
    const [isCopied, setIsCopied] = useState(false);
    const [isPatManagerOpen, setPatManagerOpen] = useState(false);
    const isRepoLinked = !!repoUrl;
    const lastEditedLabel = formatRelativeDate(lastModified);

    const handleCopy = () => {
        navigator.clipboard.writeText(id).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        });
    };
    
    return (
        <div className="flex flex-col h-full overflow-y-auto custom-scrollbar">
            {/* Workspace identity + GitHub — dim when incognito since workspace context is bypassed */}
            <div className={`transition-opacity duration-300 ${isIncognitoActive ? 'opacity-30 pointer-events-none select-none' : ''}`}>
            <div className="p-4 border-b border-border-color">
                <div className="flex justify-between items-start gap-2">
                    <h3 className="font-bold text-base text-on-surface flex-1 truncate">{workspaceName || id}</h3>
                    <button
                        type="button"
                        onClick={() => onOpenSettings(activeWorkspace)}
                        className="p-1 rounded-full text-on-surface-dim hover:text-primary hover:bg-white/5 transition-colors flex-shrink-0"
                        title="Workspace Settings"
                    >
                        <Settings size={12} />
                    </button>
                </div>
                <div className="mt-[8px] flex items-center justify-between gap-3">
                    <button onClick={handleCopy} title="Click to copy ID" className="flex items-center gap-1.5 text-on-surface-dim hover:text-on-surface transition-colors min-w-0">
                        <span className="text-xs truncate">ID: {id.length > 16 ? `${id.slice(0, 14)}…` : id}</span>
                        {isCopied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                    </button>
                    <span className="text-[10px] leading-none text-on-surface-dim whitespace-nowrap">Last Edit: {lastEditedLabel}</span>
                </div>
            </div>
            <div className="border-b border-border-color">
                <div className="flex items-center justify-between px-4 py-2.5">
                    <h4 className="flex items-center gap-2 text-xs font-semibold text-on-surface-dim uppercase tracking-wider"><Github size={12}/><span>GitHub</span></h4>
                    <button onClick={() => setPatManagerOpen(prev => !prev)} className={`p-1 rounded-full transition-colors ${isPatManagerOpen ? 'bg-primary/20 text-primary' : 'text-on-surface-dim hover:text-primary'}`} title="Configure GitHub Integration">
                        <Settings size={12} />
                    </button>
                </div>
                <AnimatePresence>
                {isPatManagerOpen && <GithubPatManager isGithubPatReady={isGithubPatReady} />}
                </AnimatePresence>
                <div className={`space-y-1 p-2.5 pt-1 ${isRepoLinked ? 'bg-emerald-500/5 border border-emerald-500/20 rounded-md mx-2 mb-2' : ''}`}>
                   {isRepoLinked ? (
                       <>
                           <div className="flex items-center gap-1.5 px-1 py-1 mb-1">
                               <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                               <span className="text-[10px] text-emerald-400 font-medium">Connected</span>
                               <span className="text-[10px] text-on-surface-dim mx-1">•</span>
                               <GitBranchPlus size={10} className="text-on-surface-dim" />
                               <span className="text-[10px] text-on-surface-dim font-mono">via-native</span>
                           </div>
                           <button
                               onClick={onInitiateForcePull}
                               disabled={isFetching}
                               className="w-full flex items-center gap-2.5 px-1 py-1.5 text-xs font-medium rounded-md hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                               title="Replace workspace with exact remote branch state (destructive)"
                           >
                               <div className="w-4 h-4 flex items-center justify-center">
                                   {isFetching ? <Loader size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
                               </div>
                               <span className="truncate">
                                   {isFetching ? 'Syncing...' : 'Hard Pull from Remote'}
                               </span>
                           </button>
                           <a href={repoUrl} target="_blank" rel="noopener noreferrer" title="View on GitHub" className="w-full flex items-center gap-2.5 px-1 py-1.5 text-xs font-medium rounded-md hover:bg-surface-dark text-on-surface transition-colors">
                               <div className="w-4 h-4 flex items-center justify-center"><ExternalLink size={14} className="text-on-surface-dim"/></div>
                               <span className="truncate">View on GitHub</span>
                           </a>
                       </>
                   ) : (
                       <>
                           <button onClick={onInitiateCreateRepo} disabled={!isGithubPatReady || isCreatingRepo} title={!isGithubPatReady ? "Add a GitHub PAT to enable" : isCreatingRepo ? "Creating repository..." : "Create a GitHub repo from this workspace"} className="w-full flex items-center gap-2.5 px-1 py-1.5 text-xs font-medium rounded-md hover:bg-surface-dark text-on-surface transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                              <div className="w-4 h-4 flex items-center justify-center">
                                  {isCreatingRepo ? <Loader size={14} className="text-on-surface-dim animate-spin"/> : <GitBranchPlus size={14} className="text-on-surface-dim"/>}
                              </div>
                              <span className="truncate">Create GitHub Repo...</span>
                           </button>
                           {fileCount === 0 && (
                               <button onClick={onInitiateClone} disabled={!isGithubPatReady || isCloning} title={!isGithubPatReady ? "Add a GitHub PAT to enable cloning" : isCloning ? "Cloning repository..." : "Clone a repository into this empty workspace"} className="w-full flex items-center gap-2.5 px-1 py-1.5 text-xs font-medium rounded-md hover:bg-surface-dark text-on-surface transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                                  <div className="w-4 h-4 flex items-center justify-center">
                                      {isCloning ? <Loader size={14} className="text-on-surface-dim animate-spin"/> : <Github size={14} className="text-on-surface-dim"/>}
                                  </div>
                                  <span className="truncate">Clone into Workspace...</span>
                               </button>
                           )}
                       </>
                   )}
                </div>
            </div>
            </div>{/* end workspace-dim */}

            {/* Tools + MCP — always active even in incognito */}
            <WorkspaceAccessControls />

            {/* Workspace actions — dim when incognito */}
            <div className={`transition-opacity duration-300 ${isIncognitoActive ? 'opacity-30 pointer-events-none select-none' : ''}`}>
            <div className="p-2.5 border-b border-border-color">
                 <h4 className="px-1.5 pb-2 flex items-center gap-2 text-xs font-semibold text-on-surface-dim uppercase tracking-wider"><Proportions size={12}/><span>Workspace Actions</span></h4>
                 <div className="space-y-1">
                    <button onClick={() => onDownloadZip(activeWorkspace)} className="w-full flex items-center gap-2 px-1 py-1.5 text-[11px] font-medium rounded-md hover:bg-surface-dark text-on-surface transition-colors">
                        <div className="w-3.5 h-3.5 flex items-center justify-center ml-0.5"><Download size={12} className="text-on-surface-dim"/></div>
                        <span className="truncate">Download Workspace Zip</span>
                    </button>
                     <button onClick={() => onOpenSettings(activeWorkspace)} className="w-full flex items-center gap-2 px-1 py-1.5 text-[11px] font-medium rounded-md hover:bg-surface-dark text-on-surface transition-colors">
                        <div className="w-3.5 h-3.5 flex items-center justify-center ml-0.5"><Settings size={12} className="text-on-surface-dim"/></div>
                        <span className="truncate">Workspace Settings</span>
                    </button>
                </div>
            </div>

            <NotificationArea notification={notification} />
            <div className="flex-grow"></div>
            </div>{/* end workspace-actions-dim */}
        </div>
    );
}

const WorkspaceSidebar = ({ workspaces, activeWorkspaceId, activeWorkspace, onSelect, onOpenSettings, onInitiateDelete, onDownloadZip, isLoading, error, isGithubPatReady, onInitiateCreate, onInitiateClone, onInitiateCreateRepo, isCloning, isCreatingRepo, notification, onInitiateForcePull, isFetching, createdCount, workspaceLimit }) => {
    const selectedWorkspace = activeWorkspace?.id ? activeWorkspace : null;
    const [isListMode, setIsListMode] = useState(() => !activeWorkspace?.id);
    const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, targetNode: null });
    const { conversations } = useConversation();

    useEffect(() => {
        if (!selectedWorkspace) {
            setIsListMode(true);
        }
    }, [selectedWorkspace]);

    useEffect(() => {
        if (activeWorkspace?.id) {
            setIsListMode(false);
            return;
        }

        setIsListMode(true);
    }, [activeWorkspaceId, activeWorkspace?.id]);

    const handleSelectWorkspace = useCallback((workspaceId) => {
        onSelect(workspaceId);
        setIsListMode(false);
    }, [onSelect]);

    const handleBackToList = useCallback(() => {
        setIsListMode(true);
    }, []);

    const handleWorkspaceContextMenu = useCallback((x, y, workspace) => {
        if (!workspace) {
            setContextMenu({ show: false, x: 0, y: 0, targetNode: null });
            return;
        }

        setContextMenu({ show: true, x, y, targetNode: workspace });
    }, []);

    const closeContextMenu = useCallback(() => {
        setContextMenu({ show: false, x: 0, y: 0, targetNode: null });
    }, []);

    const workspaceContextActions = useMemo(() => {
        if (!contextMenu.targetNode) {
            return [];
        }

        const workspace = contextMenu.targetNode;
        const displayName = workspace.isDefault ? 'Main Workspace' : (workspace.workspaceName || workspace.id);
        const lastActivity = formatRelativeDate(getWorkspaceEffectiveLastUsed(workspace, conversations));
        const rawSource = String(
            workspace.source
            || workspace.sourceType
            || workspace.importSource
            || workspace.importType
            || workspace.origin
            || workspace.createdFrom
            || ''
        ).toUpperCase();
        const sourceLabel = rawSource.includes('ZIP')
            ? 'ZIP'
            : (workspace.repoUrl || rawSource.includes('GITHUB'))
                ? 'GITHUB'
                : 'LOCAL';
        const actions = [
            { kind: 'header', label: displayName, description: workspace.id },
            { kind: 'meta', label: 'Status', valueNode: <StatusDisplay status={workspace.status} /> },
            { kind: 'meta', label: 'Last', value: lastActivity },
            { kind: 'meta', label: 'Source', value: sourceLabel },
            { separator: true },
            { label: 'Open Workspace', icon: <FolderOpen size={14} />, onClick: (targetWorkspace) => handleSelectWorkspace(targetWorkspace.id) },
            { label: 'Workspace Settings', icon: <Settings size={14} />, onClick: onOpenSettings },
            { label: 'Download Zip', icon: <Download size={14} />, onClick: onDownloadZip },
            { separator: true },
            {
                label: workspace.isDefault ? 'Reset Main Workspace' : 'Delete Workspace',
                icon: <Trash2 size={14} className="text-red-500/80" />,
                onClick: onInitiateDelete,
            },
        ];

        return actions;
    }, [contextMenu.targetNode, conversations, handleSelectWorkspace, onDownloadZip, onInitiateDelete, onOpenSettings]);

    const isInsideWorkspace = Boolean(selectedWorkspace) && !isListMode;

    return (
        <div className="h-full flex flex-col min-h-0">
            {isInsideWorkspace ? (
                <WorkspaceDetailHeader
                    workspaceName={selectedWorkspace.workspaceName || selectedWorkspace.id}
                    isDefault={Boolean(selectedWorkspace.isDefault)}
                    onBack={handleBackToList}
                />
            ) : (
                <WorkspaceListHeader onInitiateCreate={onInitiateCreate} createdCount={createdCount} workspaceLimit={workspaceLimit} />
            )}

            <AnimatePresence mode="wait">
                {isInsideWorkspace ? (
                    <motion.div
                        key={`workspace-detail-${selectedWorkspace.id}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="min-h-0 flex-1"
                    >
                        <CommandBarView
                            activeWorkspace={selectedWorkspace}
                            onOpenSettings={onOpenSettings}
                            onDownloadZip={onDownloadZip}
                            isGithubPatReady={isGithubPatReady}
                            onInitiateClone={onInitiateClone}
                            onInitiateCreateRepo={onInitiateCreateRepo}
                            isCloning={isCloning}
                            isCreatingRepo={isCreatingRepo}
                            notification={notification}
                            onInitiateForcePull={onInitiateForcePull}
                            isFetching={isFetching}
                        />
                    </motion.div>
                ) : (
                    <motion.div
                        key="workspace-list"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="min-h-0 flex-1"
                    >
                        <WorkspaceListView
                            workspaces={workspaces}
                            activeWorkspaceId={activeWorkspaceId}
                            onSelect={handleSelectWorkspace}
                            onContextMenu={handleWorkspaceContextMenu}
                            isLoading={isLoading}
                            error={error}
                            onInitiateCreate={onInitiateCreate}
                            className="h-full"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
            <ContextMenu {...contextMenu} onClose={closeContextMenu} actions={workspaceContextActions} targetNode={contextMenu.targetNode} />
        </div>
    );
};

export default WorkspaceSidebar;